import { mdiClose, mdiMagnify, mdiTune } from '@mdi/js';
import Icon from '@mdi/react';
import { Popover } from '@mui/material';
import { format } from 'date-fns';
import _ from 'lodash';
import * as React from 'react';
import { AsyncSelect, BaseInput, BaseSelect, DatePicker, ISelectOption, Spinner } from '../base';
import { AuthorDateFilter } from '../data-table/components/column-filter';
import {
    FilterMethod,
    IColumnFilterConfig,
    IColumnFiltersConfig,
    ICustomFilter,
} from '../data-table/components/filters/filters.models';
import { DataTablePresenter } from '../data-table';
import { clearTaskSearchIdCaches, TaskSearchIn } from './task-search';
import {
    PATIENT_NAME_FILTER_ID,
    clearPatientClaimIdCache,
    collectClaimIdsFromPatientClaims,
} from './patient-claim-search';
import * as S from './advanced-message-search-bar.styles';

export interface IAuthorDateSearchValue {
    author?: { method: FilterMethod; value: string };
    date?: { method: FilterMethod; value: Date };
}

export interface IMessageSearchDraft {
    subject: string;
    body: string;
    /** Full-text over coding results — resolves to coding IDs, then filterCodingId. */
    coding: string;
    purpose: string;
    categories: ISelectOption[];
    assigneeIds: string[];
    patients: ISelectOption[];
    created: IAuthorDateSearchValue;
    updated?: Date;
    priority: ISelectOption | null;
}

const SELECT_MENU_STYLES = {
    menuPortal: (base: Record<string, unknown>) => ({ ...base, zIndex: 10000 }),
};

const emptyDraft = (): IMessageSearchDraft => ({
    subject: '',
    body: '',
    coding: '',
    purpose: '',
    categories: [],
    assigneeIds: [],
    patients: [],
    created: {},
    updated: undefined,
    priority: null,
});

const POPOVER_FILTER_IDS = [
    'filterPurpose',
    'filterMessageCategoryId',
    'filterUserId',
    PATIENT_NAME_FILTER_ID,
    'filterPatientId',
    'filterPayerId',
    'filterAuthor',
    'filterCreated',
    'filterUpdated',
    'filterPriority',
] as const;

const asSelectOption = (raw: unknown): ISelectOption | null => {
    if (raw == null || raw === '') {
        return null;
    }
    if (typeof raw === 'object' && raw !== null && 'value' in raw) {
        return raw as ISelectOption;
    }
    return { label: String(raw), value: raw as string };
};

const asSelectOptions = (raw: unknown): ISelectOption[] => {
    if (raw == null || raw === '') {
        return [];
    }
    const list = Array.isArray(raw) ? raw : [raw];
    return list.map(asSelectOption).filter((opt): opt is ISelectOption => !!opt && opt.value != null && opt.value !== '');
};

const asIds = (raw: unknown): string[] => {
    return asSelectOptions(raw)
        .map(opt => String(opt.value ?? ''))
        .filter(Boolean);
};

const toDate = (raw: unknown): Date | undefined => {
    if (!raw) {
        return undefined;
    }
    if (raw instanceof Date) {
        return isNaN(raw.getTime()) ? undefined : raw;
    }
    const parsed = new Date(raw as string);
    return isNaN(parsed.getTime()) ? undefined : parsed;
};

const draftHasValues = (draft: IMessageSearchDraft): boolean => {
    return !!(
        draft.subject.trim() ||
        draft.body.trim() ||
        draft.coding.trim() ||
        draft.purpose.trim() ||
        draft.categories.length ||
        draft.assigneeIds.length ||
        draft.patients.length ||
        draft.created?.author?.value ||
        draft.created?.date?.value ||
        draft.updated ||
        (draft.priority && draft.priority.value !== undefined)
    );
};

export function readMessageSearchDraft(options: {
    searchTerm: string;
    searchIn: TaskSearchIn;
    presenter: DataTablePresenter<any>;
    priorityOptions?: ISelectOption[];
}): IMessageSearchDraft {
    const { searchTerm, searchIn, presenter, priorityOptions } = options;
    const purpose = presenter.getColumnFilter('filterPurpose');
    const subjectFilter = presenter.getColumnFilter('filterSubject');
    const subjectFromColumn =
        subjectFilter?.value != null ? String((subjectFilter.value as any)?.value ?? subjectFilter.value) : '';
    const categories = presenter.getColumnFilter('filterMessageCategoryId');
    const assignees = presenter.getColumnFilter('filterUserId');
    const patients = presenter.validFilters.filter(f => f.filterId === PATIENT_NAME_FILTER_ID);
    const author = presenter.getColumnFilter('filterAuthor');
    const created = presenter.getColumnFilter('filterCreated');
    const updated = presenter.getColumnFilter('filterUpdated');
    const priority = presenter.getColumnFilter('filterPriority');

    const authorValue = author?.value != null ? String((author.value as any)?.value ?? author.value) : '';
    const priorityRaw = asSelectOption(priority?.value);
    const matchedPriority =
        priorityRaw && priorityOptions
            ? priorityOptions.find(opt => String(opt.value) === String(priorityRaw.value)) || priorityRaw
            : priorityRaw;

    return {
        subject: searchIn === 'subject' ? searchTerm || subjectFromColumn : subjectFromColumn,
        body: searchIn === 'body' ? searchTerm : '',
        coding: searchIn === 'coding' ? searchTerm : '',
        purpose: purpose?.value != null ? String((purpose.value as any)?.value ?? purpose.value) : '',
        categories: asSelectOptions(categories?.value),
        assigneeIds: asIds(assignees?.value),
        patients: asSelectOptions(patients.flatMap(f => (Array.isArray(f.value) ? f.value : [f.value]))),
        created: {
            author: authorValue
                ? { method: (author?.filterMethod as FilterMethod) || FilterMethod.EQ, value: authorValue }
                : undefined,
            date: (() => {
                const createdDate = toDate(created?.value);
                return createdDate
                    ? {
                          method: (created?.filterMethod as FilterMethod) || FilterMethod.GTE,
                          value: createdDate,
                      }
                    : undefined;
            })(),
        },
        updated: toDate(updated?.value),
        priority: matchedPriority,
    };
}

function applyCombinedDateFilter(
    presenter: DataTablePresenter<any>,
    kind: 'created' | 'updated',
    combined?: IAuthorDateSearchValue,
): void {
    const authorFilterId = 'filterAuthor';
    const dateFilterId = kind === 'created' ? 'filterCreated' : 'filterUpdated';
    presenter.findAndRemoveFilter({ filterId: authorFilterId });
    presenter.findAndRemoveFilter({ filterId: dateFilterId });

    if (combined?.author?.value) {
        presenter.onFilterAdd({
            filterId: authorFilterId,
            filterMethod: combined.author.method,
            filterType: 'String',
            value: combined.author.value,
            label: kind === 'created' ? 'Author' : 'Last Author',
        } as ICustomFilter);
    }
    if (combined?.date?.value) {
        presenter.onFilterAdd({
            filterId: dateFilterId,
            filterMethod: combined.date.method,
            filterType: 'DateTime',
            value: combined.date.value,
            label: kind === 'created' ? 'Created' : 'Updated',
        } as ICustomFilter);
    }
}

function applyArrayFilter(
    presenter: DataTablePresenter<any>,
    filterId: string,
    filterType: ICustomFilter['filterType'],
    values: unknown[],
    label: string,
    method: FilterMethod,
): void {
    if (!values.length) {
        presenter.findAndRemoveFilter({ filterId });
        return;
    }
    presenter.findAndUpdateFilters(
        { filterId },
        values.map(
            option =>
                ({
                    filterId,
                    filterMethod: method,
                    filterType,
                    value: option,
                    label,
                }) as ICustomFilter,
        ),
    );
}

/** Apply popover-managed column filters. Does not touch type chips or message-id FTS extras. */
export function applyMessageSearchDraftFilters(
    presenter: DataTablePresenter<any>,
    draft: IMessageSearchDraft,
    options?: { includePriority?: boolean },
): void {
    const purpose = draft.purpose.trim();
    if (purpose) {
        presenter.onColumnFilterChange('filterPurpose', {
            filterId: 'filterPurpose',
            filterMethod: FilterMethod.LK,
            filterType: 'String',
            value: purpose,
            label: 'Purpose',
        });
    } else {
        presenter.findAndRemoveFilter({ filterId: 'filterPurpose' });
    }

    applyArrayFilter(
        presenter,
        'filterMessageCategoryId',
        'AsyncSelect',
        draft.categories,
        'Categories',
        FilterMethod.EQ,
    );
    applyArrayFilter(presenter, 'filterUserId', 'String', draft.assigneeIds, 'Assignees', FilterMethod.EQ);
    presenter.findAndRemoveFilter({ filterId: 'filterPatientId' });
    applyArrayFilter(presenter, PATIENT_NAME_FILTER_ID, 'AsyncSelect', draft.patients, 'Patient', FilterMethod.EQ);
    presenter.findAndRemoveFilter({ filterId: 'filterPayerId' });

    applyCombinedDateFilter(presenter, 'created', draft.created);

    presenter.findAndRemoveFilter({ filterId: 'filterUpdated' });
    if (draft.updated) {
        presenter.onFilterAdd({
            filterId: 'filterUpdated',
            filterMethod: FilterMethod.GTE,
            filterType: 'DateTime',
            value: draft.updated,
            label: 'Updated',
        } as ICustomFilter);
    }

    if (options?.includePriority) {
        const priorityValue = draft.priority;
        const treatEmpty = priorityValue != null && priorityValue.value === null;
        if (treatEmpty) {
            presenter.onColumnFilterChange('filterPriority', {
                filterId: 'filterPriority',
                filterMethod: FilterMethod.EMPTY,
                filterType: 'Select',
                value: priorityValue,
                label: 'Priority',
            } as ICustomFilter);
        } else if (priorityValue && priorityValue.value != null && priorityValue.value !== '') {
            presenter.onColumnFilterChange('filterPriority', {
                filterId: 'filterPriority',
                filterMethod: FilterMethod.EQ,
                filterType: 'Select',
                value: priorityValue,
                label: 'Priority',
            } as ICustomFilter);
        } else {
            presenter.findAndRemoveFilter({ filterId: 'filterPriority' });
        }
    }
}

export function clearMessageSearchDraftFilters(presenter: DataTablePresenter<any>): void {
    POPOVER_FILTER_IDS.forEach(filterId => presenter.findAndRemoveFilter({ filterId }));
}

export interface IAdvancedMessageSearchBarProps {
    searchTerm: string;
    searchIn: TaskSearchIn;
    presenter: DataTablePresenter<any>;
    columnFiltersConfig?: IColumnFiltersConfig;
    loadAuthorOptions?: (searchTerm: string) => Promise<ISelectOption[]>;
    currentUserId?: string;
    currentUserLabel?: string;
    onSearchCommit: (term: string, searchIn: TaskSearchIn) => void;
    /** Offer the "Coding result" search. See IMessageListRedesignedProps.showCodingSearch. */
    showCodingSearch?: boolean;
    /** Support vs provider GET /claims. Used when a Patient is selected (debounced). */
    isSupport?: boolean;
}

interface IAdvancedMessageSearchBarState {
    searchValue: string;
    popoverOpen: boolean;
    draft: IMessageSearchDraft;
    claimsLoading: boolean;
}

export class AdvancedMessageSearchBar extends React.Component<
    IAdvancedMessageSearchBarProps,
    IAdvancedMessageSearchBarState
> {
    private wrapperRef = React.createRef<HTMLDivElement>();
    private patientClaimLookupGen = 0;
    private debouncedSubjectCommit = _.debounce((): void => {
        this.commitSubjectSearch(this.state.searchValue);
    }, 1000);
    private debouncedPatientClaimLookup = _.debounce((): void => {
        void this.runPatientClaimLookup();
    }, 500);

    public state: IAdvancedMessageSearchBarState = {
        searchValue: this.props.searchIn === 'subject' ? this.props.searchTerm : '',
        popoverOpen: false,
        draft: emptyDraft(),
        claimsLoading: false,
    };

    public componentDidUpdate(prevProps: IAdvancedMessageSearchBarProps): void {
        if (
            prevProps.searchTerm !== this.props.searchTerm ||
            prevProps.searchIn !== this.props.searchIn
        ) {
            const nextBar =
                this.props.searchIn === 'subject' ? this.props.searchTerm : '';
            if (nextBar !== this.state.searchValue && !this.state.popoverOpen) {
                this.setState({ searchValue: nextBar });
            }
        }
    }

    public componentWillUnmount(): void {
        this.debouncedSubjectCommit.cancel();
        this.debouncedPatientClaimLookup.cancel();
        this.patientClaimLookupGen += 1;
    }

    private get showPriority(): boolean {
        return this.props.columnFiltersConfig?.Priority?.filterType === 'Select';
    }

    private get priorityOptions(): ISelectOption[] {
        const cfg = this.props.columnFiltersConfig?.Priority;
        return cfg && cfg.filterType === 'Select' ? cfg.options || [] : [];
    }

    private get categoriesConfig(): IColumnFilterConfig | undefined {
        return this.props.columnFiltersConfig?.Categories;
    }

    private get assigneesConfig(): IColumnFilterConfig | undefined {
        return this.props.columnFiltersConfig?.Assignees;
    }

    private get patientsConfig(): IColumnFilterConfig | undefined {
        return this.props.columnFiltersConfig?.Patient;
    }

    private patientIdsFromDraft(patients: ISelectOption[] = this.state.draft.patients): string[] {
        return patients.map(opt => String(opt.value ?? '')).filter(Boolean);
    }

    private runPatientClaimLookup = async (): Promise<void> => {
        const patientIds = this.patientIdsFromDraft();
        const gen = ++this.patientClaimLookupGen;
        if (!patientIds.length) {
            clearPatientClaimIdCache();
            if (gen === this.patientClaimLookupGen) {
                this.setState({ claimsLoading: false });
            }
            return;
        }
        this.setState({ claimsLoading: true });
        try {
            await collectClaimIdsFromPatientClaims({
                patientIds,
                isSupport: !!this.props.isSupport,
            });
        } catch {
            // Call ended — Apply is enabled again; Apply still resolves from cache or retries.
        }
        if (gen === this.patientClaimLookupGen) {
            this.setState({ claimsLoading: false });
        }
    };

    private handlePatientsChange = (list: ISelectOption[]): void => {
        this.debouncedPatientClaimLookup.cancel();
        this.setState(
            prev => ({
                draft: { ...prev.draft, patients: list },
                claimsLoading: list.length > 0,
            }),
            () => {
                if (!list.length) {
                    this.patientClaimLookupGen += 1;
                    clearPatientClaimIdCache();
                    this.setState({ claimsLoading: false });
                    return;
                }
                this.debouncedPatientClaimLookup();
            },
        );
    };

    private commitSubjectSearch = (term: string): void => {
        clearTaskSearchIdCaches();
        this.props.onSearchCommit(term, 'subject');
    };

    private handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const value = e.target.value;
        clearTaskSearchIdCaches();
        this.setState({ searchValue: value }, () => {
            if (value.trim()) {
                this.debouncedSubjectCommit();
            } else {
                this.debouncedSubjectCommit.cancel();
                this.props.onSearchCommit('', 'subject');
            }
        });
    };

    private handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
        if (e.key === 'Enter') {
            e.preventDefault();
            this.debouncedSubjectCommit.cancel();
            this.commitSubjectSearch(this.state.searchValue);
        }
    };

    private openPopover = (): void => {
        const draft = readMessageSearchDraft({
            searchTerm: this.props.searchIn === 'subject' ? this.state.searchValue || this.props.searchTerm : this.props.searchTerm,
            searchIn: this.props.searchIn !== 'subject' && !this.state.searchValue.trim()
                ? this.props.searchIn
                : this.state.searchValue.trim()
                  ? 'subject'
                  : this.props.searchIn,
            presenter: this.props.presenter,
            priorityOptions: this.priorityOptions,
        });
        if (this.state.searchValue.trim()) {
            draft.subject = this.state.searchValue;
            draft.body = '';
            draft.coding = '';
        }
        this.setState(
            { popoverOpen: true, draft, claimsLoading: draft.patients.length > 0 },
            () => {
                if (draft.patients.length) {
                    this.debouncedPatientClaimLookup.cancel();
                    void this.runPatientClaimLookup();
                }
            },
        );
    };

    private closePopover = (): void => {
        this.setState({ popoverOpen: false });
    };

    /**
     * Subject / Body / Coding are mutually exclusive: `searchIn` + `searchTerm` carry exactly one
     * search, and the three resolve to different API filters that the list cannot combine.
     * The patch decides the winner; the other two are cleared.
     */
    private updateDraft = (patch: Partial<IMessageSearchDraft>): void => {
        this.setState(prev => {
            const next = { ...prev.draft, ...patch };
            const claimed = (['subject', 'body', 'coding'] as const).find(
                key => patch[key] !== undefined && String(patch[key]).trim(),
            );
            if (claimed) {
                (['subject', 'body', 'coding'] as const)
                    .filter(key => key !== claimed)
                    .forEach(key => {
                        next[key] = '';
                    });
            }
            return { draft: next };
        });
    };

    private handleApply = (): void => {
        const { draft, claimsLoading } = this.state;
        if (claimsLoading) {
            return;
        }
        const hasBody = !!draft.body.trim();
        const hasCoding = !!draft.coding.trim();
        const hasSubject = !!draft.subject.trim();
        applyMessageSearchDraftFilters(this.props.presenter, draft, {
            includePriority: this.showPriority,
        });
        // Subject lives in searchTerm / searchExtra — drop a column subject filter so LK is not applied twice.
        this.props.presenter.findAndRemoveFilter({ filterId: 'filterSubject' });
        clearTaskSearchIdCaches();
        const searchIn: TaskSearchIn = hasBody ? 'body' : hasCoding ? 'coding' : 'subject';
        const searchTerm = hasBody
            ? draft.body.trim()
            : hasCoding
              ? draft.coding.trim()
              : hasSubject
                ? draft.subject.trim()
                : '';
        this.debouncedSubjectCommit.cancel();
        this.debouncedPatientClaimLookup.cancel();
        this.setState(
            {
                popoverOpen: false,
                searchValue: hasBody || hasCoding ? '' : searchTerm,
                claimsLoading: false,
            },
            () => {
                this.props.onSearchCommit(searchTerm, searchIn);
            },
        );
    };

    private handleClear = (): void => {
        this.debouncedSubjectCommit.cancel();
        this.debouncedPatientClaimLookup.cancel();
        this.patientClaimLookupGen += 1;
        clearTaskSearchIdCaches();
        clearPatientClaimIdCache();
        clearMessageSearchDraftFilters(this.props.presenter);
        this.props.presenter.findAndRemoveFilter({ filterId: 'filterSubject' });
        this.setState({ draft: emptyDraft(), searchValue: '', claimsLoading: false }, () => {
            this.props.onSearchCommit('', 'subject');
        });
    };

    private renderAsyncOptionsField = (
        cfg: IColumnFilterConfig | undefined,
        label: string,
        placeholder: string,
        draftKey: 'patients' | 'categories',
        onChange?: (list: ISelectOption[]) => void,
    ): React.ReactNode => {
        if (!cfg || cfg.filterType !== 'AsyncSelect' || !cfg.loadOptions) {
            return null;
        }
        return (
            <S.FormField>
                <S.Label>{label}</S.Label>
                <AsyncSelect
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={SELECT_MENU_STYLES as any}
                    isMulti={cfg.isMulti !== false}
                    defaultOptions={true}
                    cacheOptions={true}
                    loadOptions={cfg.loadOptions}
                    placeholder={placeholder}
                    value={this.state.draft[draftKey]}
                    onChange={selected => {
                        const list = Array.isArray(selected) ? selected : selected ? [selected] : [];
                        const options = list as ISelectOption[];
                        if (onChange) {
                            onChange(options);
                            return;
                        }
                        this.updateDraft({ [draftKey]: options });
                    }}
                />
            </S.FormField>
        );
    };

    private renderAssigneesField = (): React.ReactNode => {
        const cfg = this.assigneesConfig;
        if (!cfg || cfg.filterType !== 'Custom') {
            return null;
        }
        const AssigneeComponent = cfg.component;
        return (
            <S.FormField>
                <S.Label>Assignees</S.Label>
                <AssigneeComponent
                    value={this.state.draft.assigneeIds}
                    onChange={ids => this.updateDraft({ assigneeIds: Array.isArray(ids) ? ids : [] })}
                />
            </S.FormField>
        );
    };

    private renderPopover = (): React.ReactElement => {
        const { draft, claimsLoading } = this.state;
        const subjectDisabled = !!draft.body.trim() || !!draft.coding.trim();
        const bodyDisabled = !!draft.subject.trim() || !!draft.coding.trim();
        const codingDisabled = !!draft.subject.trim() || !!draft.body.trim();
        const applyDisabled = !draftHasValues(draft) || claimsLoading;

        return (
            <S.TaskSearchPopoverContent>
                <S.PopoverHeader>
                    <S.PopoverTitle>Search tasks</S.PopoverTitle>
                    <S.CloseButton onClick={this.closePopover} type="button">
                        <Icon path={mdiClose} size={0.8} />
                    </S.CloseButton>
                </S.PopoverHeader>

                <S.TaskSearchPopoverBody>
                    <S.FormGrid>
                        <S.FormField>
                            <S.Label>Subject</S.Label>
                            <BaseInput
                                value={draft.subject}
                                disabled={subjectDisabled}
                                onChange={e => this.updateDraft({ subject: e.target.value })}
                                placeholder={
                                    subjectDisabled ? 'Clear the other search to use Subject' : 'Search by subject'
                                }
                            />
                        </S.FormField>
                        <S.FormField>
                            <S.Label>Body</S.Label>
                            <BaseInput
                                value={draft.body}
                                disabled={bodyDisabled}
                                onChange={e => this.updateDraft({ body: e.target.value })}
                                placeholder={
                                    bodyDisabled ? 'Clear the other search to use Body' : 'Search in comments'
                                }
                            />
                        </S.FormField>
                        {this.props.showCodingSearch && (
                        <S.FormField>
                            <S.Label>Coding result</S.Label>
                            <BaseInput
                                value={draft.coding}
                                disabled={codingDisabled}
                                onChange={e => this.updateDraft({ coding: e.target.value })}
                                placeholder={
                                    codingDisabled
                                        ? 'Clear the other search to use Coding result'
                                        : 'Rule number, rule text, coding error…'
                                }
                            />
                        </S.FormField>
                        )}
                        {subjectDisabled || bodyDisabled || codingDisabled ? (
                            <S.FormFieldFullWidth>
                                <S.Hint>
                                    {this.props.showCodingSearch
                                        ? 'Subject, Body and Coding result cannot be used together.'
                                        : 'Subject and Body cannot be used together.'}
                                </S.Hint>
                            </S.FormFieldFullWidth>
                        ) : null}

                        <S.FormField>
                            <S.Label>Purpose</S.Label>
                            <BaseInput
                                value={draft.purpose}
                                onChange={e => this.updateDraft({ purpose: e.target.value })}
                                placeholder="Enter purpose"
                            />
                        </S.FormField>

                        {this.showPriority && (
                            <S.FormField>
                                <S.Label>Priority</S.Label>
                                <BaseSelect
                                    isClearable={true}
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                    styles={SELECT_MENU_STYLES as any}
                                    options={this.priorityOptions}
                                    value={draft.priority}
                                    placeholder="Select priority"
                                    onChange={selected => {
                                        const option = Array.isArray(selected) ? selected[0] : selected;
                                        this.updateDraft({ priority: (option as ISelectOption) || null });
                                    }}
                                />
                            </S.FormField>
                        )}

                        {this.renderAsyncOptionsField(
                            this.categoriesConfig,
                            'Categories',
                            'Select categories',
                            'categories',
                        )}
                        {this.renderAssigneesField()}
                        {this.renderAsyncOptionsField(
                            this.patientsConfig,
                            'Patient',
                            'Search by first or last name',
                            'patients',
                            this.handlePatientsChange,
                        )}

                        <S.FormFieldFullWidth>
                            <S.Label>Created</S.Label>
                            <S.CreatedFilterWrap>
                                <AuthorDateFilter
                                    value={draft.created}
                                    onChange={value => this.updateDraft({ created: value || {} })}
                                    loadOptions={this.props.loadAuthorOptions}
                                    currentUserId={this.props.currentUserId}
                                    currentUserLabel={this.props.currentUserLabel}
                                />
                            </S.CreatedFilterWrap>
                        </S.FormFieldFullWidth>

                        <S.FormField>
                            <S.Label>Updated</S.Label>
                            <DatePicker
                                value={draft.updated ? format(draft.updated, 'yyyy-MM-dd') : undefined}
                                onDateSelected={date => this.updateDraft({ updated: date })}
                                format="yyyy-MM-dd"
                                inputProps={{ placeholder: 'On or after' }}
                            />
                        </S.FormField>
                    </S.FormGrid>
                </S.TaskSearchPopoverBody>

                <S.SplitFooter>
                    <S.FooterActions>
                    <S.ClearButton onClick={this.handleClear} type="button">
                        Clear
                    </S.ClearButton>
                    <S.SubmitButton onClick={this.handleApply} disabled={applyDisabled} type="button">
                            {claimsLoading ? (
                                <S.ApplySpinnerWrap>
                                    <Spinner size="16px" />
                                </S.ApplySpinnerWrap>
                            ) : (
                                'Apply'
                            )}
                        </S.SubmitButton>
                    </S.FooterActions>
                </S.SplitFooter>
            </S.TaskSearchPopoverContent>
        );
    };

    public render(): React.ReactElement {
        const { popoverOpen, searchValue } = this.state;

        return (
            <S.TaskSearchBarContainer>
                <S.SearchInputWrapper ref={this.wrapperRef} data-popover-open={popoverOpen}>
                    <S.SearchIcon path={mdiMagnify} size={0.75} />
                    <BaseInput
                        value={searchValue}
                        onChange={this.handleSearchChange}
                        onKeyDown={this.handleSearchKeyDown}
                        placeholder={
                            this.props.searchIn !== 'subject' && !searchValue.trim()
                                ? `${this.props.searchIn === 'coding' ? 'Coding result' : 'Body'} filter applied — type to search by subject`
                                : 'Search'
                        }
                        onOption={this.openPopover}
                        optionPath={mdiTune}
                    />
                </S.SearchInputWrapper>
                <Popover
                    anchorEl={this.wrapperRef.current}
                    open={popoverOpen}
                    onClose={this.closePopover}
                    disableRestoreFocus
                    disableEnforceFocus
                    disableAutoFocus
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    slotProps={{
                        paper: {
                            style: {
                                borderRadius: '12px',
                                boxShadow: '0px 8px 16px 0px rgba(0, 0, 0, 0.12)',
                                marginTop: '8px',
                                maxHeight: 'calc(100vh - 100px)',
                                display: 'flex',
                                flexDirection: 'column',
                            },
                        },
                    }}
                >
                    {this.renderPopover()}
                </Popover>
            </S.TaskSearchBarContainer>
        );
    }
}
